import { ReactNode } from "react";
import { Invoice, InvoiceLineItem, InvoiceStatus, PaymentMethod } from "../types";

export function calcInvoice(items: InvoiceLineItem[]): { subtotal: number; totalDiscount: number; totalTax: number; total: number } {
    let subtotal = 0, totalDiscount = 0, totalTax = 0;
    for (const item of items) {
        const lineGross = item.qty * item.unitPrice;
        const disc = lineGross * (item.discount / 100);
        const lineNet = lineGross - disc;
        const tax = lineNet * (item.taxRate / 100);
        subtotal += lineGross;
        totalDiscount += disc;
        totalTax += tax;
    }
    return { subtotal, totalDiscount, totalTax, total: subtotal - totalDiscount + totalTax };
}

export function makeInvoice(
    i: number,
    number: string,
    quoteRef: string | null,
    company: string, contact: string, email: string, address: string, country: string, vat: string,
    items: { desc: string; sku: string; qty: number; price: number; disc?: number; tax?: number }[],
    status: InvoiceStatus,
    issued: string, due: string, paid: string | null,
    method: PaymentMethod | null,
    terms: string,
    notes: string,
): Invoice {
    const lineItems: InvoiceLineItem[] = items.map((it) => ({
        description: it.desc, sku: it.sku, qty: it.qty, unitPrice: it.price,
        discount: it.disc ?? 0, taxRate: it.tax ?? 0,
    }));
    const { subtotal, totalDiscount, totalTax, total } = calcInvoice(lineItems);
    const amountPaid = paid ? total : (status === "paid" ? total : 0);
    return {
        id: `inv-${String(i + 1).padStart(3, "0")}`,
        invoiceNumber: number,
        quoteRef,
        billTo: { companyName: company, contactName: contact, email, address, country, vatNumber: vat },
        lineItems, currency: "USD", subtotal, totalDiscount, totalTax, total,
        amountPaid, amountDue: total - amountPaid,
        status, issueDate: issued, dueDate: due, paidDate: paid,
        paymentMethod: method, paymentTerms: terms, notes,
        createdAt: new Date(issued).toISOString(),
        updatedAt: new Date(paid ?? due).toISOString(),
    };
}

export function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtCurrency(val: number, currency = "USD"): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}

export function highlight(text: string, q: string): ReactNode {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (<>{text.slice(0, idx)}<mark style={{ background: "rgba(191,61,12,0.3)", color: "var(--or)", borderRadius: 2, padding: "0 1px" }}>{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>);
}

export function daysUntil(iso: string): number {
    return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}